import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  Stack,
  TextField,
  Snackbar
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactMarkdown from 'react-markdown';
import { apiClient } from '../../utils/api';
import { useAsyncJob } from '../../hooks/useAsyncJob';
import { linkComponentToOverview } from '../../utils/projectOverviewService';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

const SOWInsightsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project, refreshOverview } = useOutletContext();
  const [sowInsights, setSowInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const { startPolling } = useAsyncJob();
  const { startPolling: startSectionsPolling } = useAsyncJob();
  const { startPolling: startSummaryPolling } = useAsyncJob();
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmAction, setConfirmAction] = useState(null);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // Fire the background job that generates all prepare-phase sections
  const triggerSectionGeneration = async () => {
    try {
      setAutoGenerating(true);
      const resp = await apiClient.post(`/ai/generate-prepare-sections/${projectId}`);
      if (!resp.data?.job_id) {
        setAutoGenerating(false);
        return;
      }
      const result = await startSectionsPolling(
        resp.data.job_id,
        null,
        'Generating overview sections…',
        { hidden: true }
      );
      if (result?.overview_ai_summary?.insights) {
        setSowInsights(result.overview_ai_summary.insights);
      }
      if (result?.sections?.overview_ai_summary === 'ok') {
        showSnackbar('Successfully generated Project Insights, overview sections, and Project Overview Summary', 'success');
      } else {
        showSnackbar('Successfully generated Project Insights and overview sections', 'success');
      }
      if (refreshOverview) refreshOverview();
    } catch (e) {
      showSnackbar('Some sections could not be generated — you can retry from each page.', 'warning');
    } finally {
      setAutoGenerating(false);
    }
  };

  // Core analysis logic
  const runAnalysis = async () => {
    try {
      setAnalyzing(true);
      setError('');
      // Start the job — backend returns immediately with a job_id
      const response = await apiClient.post(`/ai/extract-sow-insights/${projectId}`);
      const { job_id } = response.data;
      if (!job_id) {
        // Older backend format (shouldn't happen in prod but handle gracefully)
        if (response.data.success) {
          setSowInsights(response.data.insights);
          showSnackbar('Project Insights generated successfully', 'success');
        } else {
          setError(response.data.error || 'Analysis failed');
        }
        return;
      }
      // Poll until done
      const result = await startPolling(
        job_id,
        null,
        'Generating Project Insights...',
        { hidden: true }
      );
      if (result?.success) {
        setSowInsights(result.insights);
        if (result.sow_insights?.id) {
          await linkComponentToOverview(projectId, 'sow_insights', result.sow_insights.id);
        }
        if (refreshOverview) refreshOverview();
        triggerSectionGeneration();
      } else {
        setError(result?.error || 'Analysis returned no data');
      }
    } catch (error) {
      console.error('Error analyzing SOW:', error);
      setError(error.message || error.response?.data?.error || 'Error analyzing document');
    } finally {
      setAnalyzing(false);
    }
  };

  // Load existing Project Insights. Generation is always user-confirmed.
  useEffect(() => {
    const fetchInsights = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`/projects/${projectId}/sow-insights`);
        if (response.data.has_insights) {
          setSowInsights(response.data.insights);
        }
      } catch (error) {
        console.error('Error loading SOW insights:', error);
      }
      setLoading(false);
    };
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleAnalyzeSOW = () => setConfirmAction('insights');
  const handleNext = () => navigate(`/projects/${projectId}/overview/scope`);

  const handleGenerateOverviewSummary = async () => {
    setSummaryGenerating(true);
    setError('');
    try {
      const response = await apiClient.post(`/projects/${projectId}/sow-insights/overview-ai-summary`);
      if (response.data?.job_id) {
        const result = await startSummaryPolling(
          response.data.job_id,
          null,
          'Generating Project Overview Summary...',
          { hidden: true }
        );
        if (result?.success) {
          setSowInsights(prev => ({
            ...(prev || {}),
            overview_ai_summary: result.overview_ai_summary,
            overview_ai_summary_generated_at: result.overview_ai_summary_generated_at,
          }));
          showSnackbar('Project Overview Summary generated successfully!', 'success');
          if (refreshOverview) refreshOverview();
        } else {
          showSnackbar(result?.message || 'Failed to generate Project Overview Summary', 'error');
        }
      } else if (response.data?.success) {
        setSowInsights(prev => ({
          ...(prev || {}),
          overview_ai_summary: response.data.overview_ai_summary,
          overview_ai_summary_generated_at: response.data.overview_ai_summary_generated_at,
        }));
        showSnackbar('Project Overview Summary generated successfully!', 'success');
      } else {
        showSnackbar(response.data?.message || 'Failed to generate Project Overview Summary', 'error');
      }
    } catch (err) {
      console.error('Error generating Project Overview Summary:', err);
      showSnackbar(err.response?.data?.message || err.message || 'Error generating Project Overview Summary', 'error');
    } finally {
      setSummaryGenerating(false);
    }
  };

  // Enter edit mode — deep copy current insights into editData
  const handleEdit = () => {
    setEditData(JSON.parse(JSON.stringify(sowInsights)));
    setIsEditing(true);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditData(null);
    setIsEditing(false);
  };

  // Save edited insights
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.post(`/projects/${projectId}/sow-insights`, {
        insights: editData,
      });
      if (response.status === 200) {
        setSowInsights(editData);
        setIsEditing(false);
        setEditData(null);
        showSnackbar('Project Insights saved successfully!');
        if (refreshOverview) refreshOverview();
      } else {
        showSnackbar(response.data?.message || 'Failed to save', 'error');
      }
    } catch (err) {
      console.error('Error saving SOW insights:', err);
      showSnackbar(err.response?.data?.message || 'Error saving insights', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Helpers to edit list fields
  const updateListItem = (field, index, value) => {
    setEditData(prev => {
      const list = [...(prev[field] || [])];
      if (typeof list[index] === 'string') {
        list[index] = value;
      } else {
        // object item — update the primary display key
        const key = Object.keys(list[index])[0];
        list[index] = { ...list[index], [key]: value };
      }
      return { ...prev, [field]: list };
    });
  };

  const addListItem = (field, template) => {
    setEditData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), template],
    }));
  };

  const removeListItem = (field, index) => {
    setEditData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const getItemLabel = (item) =>
    typeof item === 'string' ? item : item.description || item.title || item.name || item.milestone || item.phase || item.risk || '';

  const hasSow = !!(project?.sow_document_id || project?.documents?.sow?.length > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <AutoAwesomeIcon sx={{ mr: 1, color: '#F5821F' }} />
              Project Insights
            </Typography>
            {sowInsights && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Generated"
                color="success"
                size="small"
              />
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            {sowInsights && !isEditing && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Box>

        <Typography variant="body1" paragraph>
          Extracted insights from the project documents.
          These insights help auto-populate other sections of the project overview.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {hasSow && (
          <Alert severity="info" icon={<DescriptionIcon />} sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Project Document Uploaded</strong>
            </Typography>
            {project.documents?.sow?.[0] && (
              <Typography variant="caption">
                File: {project.documents.sow[0].title || project.documents.sow[0].document_name}
              </Typography>
            )}
          </Alert>
        )}

        {hasSow && !isEditing && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will extract the project summary, requirements, key features, timeline, stakeholders, and risks from the project documents.
            </Alert>
            <Button
              variant="contained"
              startIcon={(loading || analyzing || autoGenerating) ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleAnalyzeSOW}
              disabled={loading || analyzing || autoGenerating}
              sx={{ backgroundColor: '#F5821F', '&:hover': { backgroundColor: '#d4701a' } }}
            >
              {loading
                ? 'Loading...'
                : (analyzing || autoGenerating)
                ? 'Generating...'
                : sowInsights
                ? 'Regenerate Project Insights'
                : 'Generate Project Insights'}
            </Button>
          </>
        )}
      </Paper>

      {/* ── EDIT MODE ── */}
      {isEditing && editData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Editing Project Insights
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {/* Executive Summary */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>📝 Executive Summary</Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={editData.summary || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, summary: e.target.value }))}
                variant="outlined"
              />
            </Grid>

            {/* Requirements */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>📋 Requirements</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addListItem('requirements', '')}>
                  Add
                </Button>
              </Box>
              <Stack spacing={1}>
                {(editData.requirements || []).map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      value={getItemLabel(item)}
                      onChange={(e) => updateListItem('requirements', i, e.target.value)}
                    />
                    <IconButton size="small" color="error" onClick={() => removeListItem('requirements', i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Key Features */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>⭐ Key Features</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addListItem('key_features', '')}>
                  Add
                </Button>
              </Box>
              <Stack spacing={1}>
                {(editData.key_features || []).map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      value={getItemLabel(item)}
                      onChange={(e) => updateListItem('key_features', i, e.target.value)}
                    />
                    <IconButton size="small" color="error" onClick={() => removeListItem('key_features', i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Timeline */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>📅 Timeline</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addListItem('timeline', { milestone: '', date: '' })}>
                  Add
                </Button>
              </Box>
              <Stack spacing={1}>
                {(editData.timeline || []).map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Milestone"
                        value={typeof item === 'string' ? item : (item.milestone || item.phase || '')}
                        onChange={(e) => {
                          const updated = typeof item === 'string' ? e.target.value : { ...item, milestone: e.target.value };
                          setEditData(prev => {
                            const list = [...prev.timeline];
                            list[i] = updated;
                            return { ...prev, timeline: list };
                          });
                        }}
                        sx={{ mb: 0.5 }}
                      />
                      {typeof item !== 'string' && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Date"
                          value={item.date || item.target_date || ''}
                          onChange={(e) => {
                            setEditData(prev => {
                              const list = [...prev.timeline];
                              list[i] = { ...list[i], date: e.target.value };
                              return { ...prev, timeline: list };
                            });
                          }}
                        />
                      )}
                    </Box>
                    <IconButton size="small" color="error" onClick={() => removeListItem('timeline', i)} sx={{ mt: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Stakeholders */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>👥 Stakeholders</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addListItem('stakeholders', '')}>
                  Add
                </Button>
              </Box>
              <Stack spacing={1}>
                {(editData.stakeholders || []).map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={typeof item === 'string' ? item : (item.name || '')}
                      onChange={(e) => updateListItem('stakeholders', i, e.target.value)}
                    />
                    <IconButton size="small" color="error" onClick={() => removeListItem('stakeholders', i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Risks */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'warning.main' }}>⚠️ Risks & Considerations</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addListItem('risks', { risk: '', mitigation: '' })}>
                  Add
                </Button>
              </Box>
              <Stack spacing={1}>
                {(editData.risks || []).map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        label="Risk / Description"
                        value={typeof item === 'string' ? item : (item.description || item.risk || '')}
                        onChange={(e) => {
                          const updated = typeof item === 'string' ? e.target.value : { ...item, risk: e.target.value, description: e.target.value };
                          setEditData(prev => {
                            const list = [...prev.risks];
                            list[i] = updated;
                            return { ...prev, risks: list };
                          });
                        }}
                        sx={{ mb: 0.5 }}
                      />
                      {typeof item !== 'string' && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Mitigation"
                          value={item.mitigation || ''}
                          onChange={(e) => {
                            setEditData(prev => {
                              const list = [...prev.risks];
                              list[i] = { ...list[i], mitigation: e.target.value };
                              return { ...prev, risks: list };
                            });
                          }}
                        />
                      )}
                    </Box>
                    <IconButton size="small" color="error" onClick={() => removeListItem('risks', i)} sx={{ mt: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── VIEW MODE ── */}
      {sowInsights && !isEditing && (
        <>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            Project Documents Analysis complete! Review the extracted insights below.
          </Alert>

          <Grid container spacing={3}>
            {sowInsights.summary && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>📝 Executive Summary</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {sowInsights.summary}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {sowInsights.requirements && sowInsights.requirements.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📋 Requirements ({sowInsights.requirements.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <List dense>
                      {sowInsights.requirements.map((req, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={typeof req === 'string' ? req : req.description || req.title} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {sowInsights.key_features && sowInsights.key_features.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      ⭐ Key Features ({sowInsights.key_features.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <List dense>
                      {sowInsights.key_features.map((feature, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={typeof feature === 'string' ? feature : feature.name || feature.title} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {sowInsights.timeline && sowInsights.timeline.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📅 Timeline ({sowInsights.timeline.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <List dense>
                      {sowInsights.timeline.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={typeof item === 'string' ? item : item.milestone || item.phase}
                            secondary={item.date || item.target_date}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {sowInsights.stakeholders && sowInsights.stakeholders.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      👥 Stakeholders ({sowInsights.stakeholders.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {sowInsights.stakeholders.map((stakeholder, index) => (
                        <Chip
                          key={index}
                          label={typeof stakeholder === 'string' ? stakeholder : stakeholder.name}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {sowInsights.risks && sowInsights.risks.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="warning.main">
                      ⚠️ Risks & Considerations ({sowInsights.risks.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <List dense>
                      {sowInsights.risks.map((risk, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={typeof risk === 'string' ? risk : risk.description || risk.risk}
                            secondary={risk.mitigation}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6">
                        Project Overview Summary
                      </Typography>
                      {sowInsights.overview_ai_summary && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Generated"
                          color="success"
                          size="small"
                        />
                      )}
                      {sowInsights.overview_ai_summary_generated_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                          Last generated: {new Date(sowInsights.overview_ai_summary_generated_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This will generate a concise Project Overview summary from Project Insights, project scope, requirements, stakeholders, timeline, and uploaded documents.
                  </Alert>
                  <Button
                    variant="contained"
                    startIcon={summaryGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={() => setConfirmAction('summary')}
                    disabled={summaryGenerating}
                    sx={{ mb: 2, backgroundColor: '#F5821F', '&:hover': { backgroundColor: '#d4701a' } }}
                  >
                    {summaryGenerating
                      ? 'Generating...'
                      : sowInsights.overview_ai_summary
                      ? 'Regenerate Project Overview Summary'
                      : 'Generate Project Overview Summary'}
                  </Button>
                  {sowInsights.overview_ai_summary ? (
                    <Box
                      sx={{
                        '& p': { mt: 0, mb: 1.25 },
                        '& ul': { mt: 0.5, mb: 1.5, pl: 3 },
                        '& li': { mb: 0.5 },
                        '& strong': { color: 'text.primary' },
                      }}
                    >
                      <ReactMarkdown>{sowInsights.overview_ai_summary}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      The summary is generated automatically after Project Insights review and overview section generation finish.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>✅ What's Next?</Typography>
            <Typography variant="body2" paragraph>
              Use these insights to populate the remaining sections. Click below to continue with Project Scope.
            </Typography>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleNext}>
              Next: Define Project Scope
            </Button>
          </Paper>
        </>
      )}

      {!sowInsights && hasSow && !analyzing && (
        <Alert severity="info">
          Click <strong>Generate Project Insights</strong> to extract insights from the uploaded documents.
        </Alert>
      )}

      <SafetyConfirmDialog
        open={confirmAction === 'insights'}
        title={sowInsights ? 'Regenerate Project Insights?' : 'Generate Project Insights?'}
        warning={sowInsights ? 'Existing Project Insights and generated overview content will be replaced.' : 'This action uses AI across multiple project sections.'}
        paragraphs={sowInsights ? [
          'This action will use AI to regenerate Project Insights, Prepare-phase overview sections, and the Project Overview Summary.',
          'Any manual edits made to the existing Project Insights or generated overview content will be permanently lost.'
        ] : [
          'This action will use AI to analyze the uploaded Project Documents and generate Project Insights, Prepare-phase overview sections, and the Project Overview Summary. This may take several minutes and consume significant AI resources.'
        ]}
        confirmLabel={sowInsights ? 'Yes, Regenerate' : 'Yes, Generate'}
        loading={analyzing || autoGenerating}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => { setConfirmAction(null); runAnalysis(); }}
      />

      <SafetyConfirmDialog
        open={confirmAction === 'summary'}
        title={sowInsights?.overview_ai_summary ? 'Regenerate Project Overview Summary?' : 'Generate Project Overview Summary?'}
        warning={sowInsights?.overview_ai_summary ? 'The existing Project Overview Summary will be replaced.' : 'This action uses AI.'}
        paragraphs={sowInsights?.overview_ai_summary ? [
          'This action will use AI to replace the existing Project Overview Summary.',
          'Any manual edits made to the current summary will be permanently lost.'
        ] : [
          'This action will use AI to generate a summary using the currently available project information.'
        ]}
        confirmLabel={sowInsights?.overview_ai_summary ? 'Yes, Regenerate' : 'Yes, Generate'}
        loading={summaryGenerating}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => { setConfirmAction(null); handleGenerateOverviewSummary(); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SOWInsightsPage;
